/**
 * PageHeaderActionsBar – Title + description + CTA row (legacy API used on billing, tasks, etc.)
 *
 * Props:
 *   title          {string}              Required. Page title.
 *   description    {string}              Optional. Subtitle / description.
 *   primaryAction  {{ label, onClick, icon, disabled }} Optional. Main CTA button.
 *   secondaryActions [{ label, onClick, icon, variant }] Optional. Secondary buttons.
 *   children                             Optional. Extra right-side content (e.g. badges).
 */
import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

const PageHeaderActionsBar = ({
  title,
  description,
  primaryAction,
  secondaryActions = [],
  children,
  sx = {},
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: { xs: 'flex-start', sm: 'center' },
      flexWrap: 'wrap',
      gap: 2,
      mb: 4,
      ...sx,
    }}
  >
    <Box>
      <Typography
        variant="h4"
        sx={{ fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }}
      >
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      )}
    </Box>

    {(primaryAction || secondaryActions.length > 0 || children) && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        {secondaryActions.map((action, i) => (
          <Button
            key={i}
            variant={action.variant || 'outlined'}
            size={action.size || 'medium'}
            startIcon={action.icon}
            onClick={action.onClick}
            disabled={action.disabled}
          >
            {action.label}
          </Button>
        ))}
        {primaryAction && (
          <Button
            variant="contained"
            size={primaryAction.size || 'medium'}
            startIcon={primaryAction.icon !== undefined ? primaryAction.icon : <AddIcon />}
            onClick={primaryAction.onClick}
            disabled={primaryAction.disabled}
          >
            {primaryAction.label}
          </Button>
        )}
        {children}
      </Box>
    )}
  </Box>
);

export default PageHeaderActionsBar;
